// ==========================================================================
// lib/reminders.ts — reminders and what's due (FF-056, 10 Oct 2026)
//   James: "renewals and reminders … reminders just handy to have". A repeating
//   reminder moves to its next date when it's done; a one-off is finished.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addMonths, differenceInCalendarDays, format, parseISO } from "date-fns";
import { supabase } from "./supabase";
import { useHouseholdId } from "./household";

export type ReminderKind = "rego" | "insurance" | "licence" | "passport" | "warranty" | "service" | "subscription" | "tax" | "health" | "other";
export type Every = "none" | "month" | "quarter" | "half_year" | "year";
export type Reminder = {
  id: string; title: string; kind: ReminderKind; due_on: string; every: Every; warn_days: number; asset_id: string | null; person_id: string | null;
  amount_cents: number | null; note: string | null; last_done_on: string | null; done_on: string | null;
};
export const KINDS: { key: ReminderKind; label: string; emoji: string }[] = [
  { key: "rego", label: "Rego", emoji: "🚗" }, { key: "insurance", label: "Insurance", emoji: "🛡️" }, { key: "licence", label: "Licence", emoji: "🪪" },
  { key: "passport", label: "Passport", emoji: "🛂" }, { key: "warranty", label: "Warranty", emoji: "🧾" }, { key: "service", label: "Service", emoji: "🔧" },
  { key: "subscription", label: "Subscription", emoji: "🔁" }, { key: "tax", label: "Tax", emoji: "🏛️" }, { key: "health", label: "Check-up", emoji: "🩺" }, { key: "other", label: "Other", emoji: "📌" },
];
export const kindOf = (k: ReminderKind) => KINDS.find((x) => x.key === k) ?? KINDS[KINDS.length - 1];
export const EVERY: { key: Every; label: string; months: number }[] = [
  { key: "none", label: "Just once", months: 0 }, { key: "month", label: "Every month", months: 1 }, { key: "quarter", label: "Every 3 months", months: 3 },
  { key: "half_year", label: "Every 6 months", months: 6 }, { key: "year", label: "Every year", months: 12 },
];
export const daysTo = (iso: string) => differenceInCalendarDays(parseISO(iso), new Date());
/** "Overdue 3 days", "Today", "Tomorrow", "In 12 days", "Fri 30 Oct". */
export function dueText(iso: string): string {
  const n = daysTo(iso);
  if (n < 0) return `Overdue ${-n} day${n === -1 ? "" : "s"}`;
  if (n === 0) return "Due today";
  if (n === 1) return "Due tomorrow";
  if (n <= 21) return `In ${n} days`;
  return format(parseISO(iso), "EEE d MMM yyyy");
}

export function useReminders() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["reminders", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("reminders").select("id,title,kind,due_on,every,warn_days,asset_id,person_id,amount_cents,note,last_done_on,done_on")
        .eq("household_id", hid).is("archived_at", null).order("due_on");
      if (error) throw error;
      return (data as Reminder[]).map((r) => ({ ...r, amount_cents: r.amount_cents === null ? null : Number(r.amount_cents) }));
    },
  });
}

export type ReminderDraft = Omit<Reminder, "id" | "last_done_on" | "done_on"> & { id?: string };
export function useSaveReminder() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (d: ReminderDraft & { archive?: boolean }) => {
      const { id, archive, ...row } = d;
      const clean = { ...row, title: row.title.trim(), note: row.note?.trim() || null };
      if (id) { const { error } = await supabase.from("reminders").update(archive ? { archived_at: new Date().toISOString() } : clean).eq("id", id); if (error) throw error; return; }
      const { error } = await supabase.from("reminders").insert({ ...clean, household_id: hid });
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["reminders", hid] }),
  });
}

/** Done: a repeating one rolls on to its next date; a one-off is finished. */
export function useDoneReminder() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (r: Reminder) => {
      const today = format(new Date(), "yyyy-MM-dd");
      const months = EVERY.find((e) => e.key === r.every)?.months ?? 0;
      const row = months ? { last_done_on: today, due_on: format(addMonths(parseISO(r.due_on), months), "yyyy-MM-dd") } : { last_done_on: today, done_on: today };
      const { error } = await supabase.from("reminders").update(row).eq("id", r.id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["reminders", hid] }),
  });
}
